import React, { useState, useEffect } from 'react';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import Dashboard from './components/Dashboard';
import { User } from './types/User';
import { AuthService } from './services/authService';

const PASSWORD_RECOVERY_KEY = 'lm-password-recovery';

if (typeof window !== 'undefined') {
  const { hash, pathname } = window.location;
  if (pathname === '/reset-password' || hash.includes('type=recovery')) {
    sessionStorage.setItem(PASSWORD_RECOVERY_KEY, '1');
  }
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'login' | 'register' | 'dashboard' | 'reset-password'>('login');

  const isPasswordRecovery = () => sessionStorage.getItem(PASSWORD_RECOVERY_KEY) === '1';

  useEffect(() => {
    // Check if user is already logged in with Supabase
    const initializeApp = async () => {
      setIsLoading(true);
      if (isPasswordRecovery()) {
        setCurrentView('reset-password');
        setIsLoading(false);
        return;
      }
      // Check if user is already logged in
      const user = await AuthService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        setIsAuthenticated(true);
        setCurrentView('dashboard');
      }
      setIsLoading(false);
    };
    
    initializeApp();
  }, []);

  // Separate effect for URL handling to prevent infinite loops
  useEffect(() => {
    // Check URL for registration page
    const path = window.location.pathname;
    if (path === '/register' && !isAuthenticated) {
      setCurrentView('register');
    }
    
    // Handle navigation
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/register' && !isAuthenticated) {
        setCurrentView('register');
      }
      else if (!isAuthenticated && !isPasswordRecovery()) {
        setCurrentView('login');
      }
      setIsLoading(false);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated]);

  const handleLogin = (user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    setCurrentView('dashboard');
    window.history.pushState({}, '', '/');
  };

  const handleRegister = (user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    setCurrentView('dashboard');
    window.history.pushState({}, '', '/');
  };

  const handleLogout = async () => {
    await AuthService.logout();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setCurrentView('login');
    window.history.pushState({}, '', '/');
  };

  const handleUpdateUser = (updatedUser: User) => {
    setCurrentUser(updatedUser);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  if (currentView === 'reset-password') {
    return <ResetPasswordPage onDone={() => {
      sessionStorage.removeItem(PASSWORD_RECOVERY_KEY);
      setCurrentUser(null);
      setIsAuthenticated(false);
      setCurrentView('login');
    }} />;
  }

  if (!isAuthenticated && currentView === 'login') {
    return <LoginPage onLogin={handleLogin} onNavigateToRegister={() => {
      setCurrentView('register');
      window.history.pushState({}, '', '/register');
    }} />;
  }

  if (!isAuthenticated && currentView === 'register') {
    return <RegisterPage onRegister={handleRegister} onBackToLogin={() => setCurrentView('login')} />;
  }

  return (
    <Dashboard 
      currentUser={currentUser!} 
      onLogout={handleLogout}
      onUpdateUser={handleUpdateUser}
    />
  );
}

export default App;